// src/Pages/Admin/ClientBilling/ClientBillingDetails.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { subscriptionAdminAPI } from "../../../utils/api/subscriptionAPI";
import "./ClientBillingDetails.scss";

interface BillingPeriod {
  _id: string;
  periodMonth: string;
  calculatedPrice: number;
  currency: string;
  status: string;
  dueDate: string;
  paidAt?: string;
  employeeCount: number;
  canPay?: boolean;
}

const ClientBillingDetails: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [periods, setPeriods] = useState<BillingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod | null>(
    null
  );
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    method: "BANK_TRANSFER",
    paidAt: new Date().toISOString().split("T")[0],
    reference: "",
    note: "",
  });

  useEffect(() => {
    fetchBillingPeriods();
  }, [clientId]);

  const fetchBillingPeriods = async () => {
    try {
      setLoading(true);
      const response = await subscriptionAdminAPI.getClientBillingPeriods(
        clientId!
      );
      setPeriods(response.data.data.periods);
    } catch (error) {
      console.error("Error fetching periods:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedPeriod) return;

    try {
      await subscriptionAdminAPI.markBillingPeriodPaid(
        selectedPeriod._id,
        paymentForm
      );
      alert("Payment recorded successfully!");
      setShowPaymentModal(false);
      fetchBillingPeriods();
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to record payment");
    }
  };

  const openPaymentModal = (period: BillingPeriod) => {
    setSelectedPeriod(period);
    setPaymentForm({
      ...paymentForm,
      amount: period.calculatedPrice,
    });
    setShowPaymentModal(true);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="client-billing-details">
      <h2>Client Billing Details</h2>

      <table className="table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Amount</th>
            <th>Employees</th>
            <th>Status</th>
            <th>Due Date</th>
            <th>Paid At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((period) => (
            <tr key={period._id}>
              <td>{period.periodMonth}</td>
              <td>
                {period.calculatedPrice} {period.currency}
              </td>
              <td>{period.employeeCount}</td>
              <td>
                <span className={`badge badge-${period.status.toLowerCase()}`}>
                  {period.status}
                </span>
              </td>
              <td>{new Date(period.dueDate).toLocaleDateString()}</td>
              <td>
                {period.paidAt
                  ? new Date(period.paidAt).toLocaleDateString()
                  : "—"}
              </td>
              <td>
                {(
                  period.canPay !== undefined
                    ? period.canPay
                    : period.status === "PENDING" ||
                      period.status === "UNPAID" ||
                      period.status === "OVERDUE"
                ) ? (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => openPaymentModal(period)}
                  >
                    Mark as Paid
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Payment Modal */}
      {showPaymentModal && selectedPeriod && (
        <div
          className="modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Record Payment</h3>
            <p>Period: {selectedPeriod.periodMonth}</p>

            <div className="form-group">
              <label>Amount</label>
              <input
                type="number"
                className="form-control"
                value={paymentForm.amount}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    amount: Number(e.target.value),
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>Payment Method</label>
              <select
                className="form-control"
                value={paymentForm.method}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, method: e.target.value })
                }
              >
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CASH">Cash</option>
                <option value="CHECK">Check</option>
                <option value="CREDIT_CARD">Credit Card</option>
                <option value="MANUAL_ADJUSTMENT">Manual Adjustment</option>
              </select>
            </div>

            <div className="form-group">
              <label>Paid At</label>
              <input
                type="date"
                className="form-control"
                value={paymentForm.paidAt}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, paidAt: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Reference</label>
              <input
                type="text"
                className="form-control"
                placeholder="Transaction ID or reference number"
                value={paymentForm.reference}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, reference: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Note</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Internal note..."
                value={paymentForm.note}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, note: e.target.value })
                }
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleMarkAsPaid}>
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBillingDetails;
